import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import ExportOrdersExcel from "./ExportOrdersExcel";
// import type { OrderLocation } from "./GoogleMapComponent";
import type { OrderLocation } from "./InfoPopup";
import { getFlagReasonsByCustomer } from "../../utils/flag-reason";
import { dateForCalendar } from "../../utils/delivery-planning";

// These API fields are calendar dates; avoid UTC parsing that can shift the day.
function assignmentDateText(value?: string | null): string {
  if (!value) return "—";
  const date = dateForCalendar(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

/* ---------- Age helpers ---------- */
const AGE_BUCKETS = [
  { key: "new",    label: "≤ 3", color: "#2e7d32", bg: "rgba(46,125,50,0.10)" },   // green
  { key: "recent", label: "4–7", color: "#ff9800", bg: "rgba(255,152,0,0.12)" },   // orange
  { key: "old",    label: "8+",  color: "#d32f2f", bg: "rgba(211,47,47,0.12)" },   // red
] as const;
type AgeKey = typeof AGE_BUCKETS[number]["key"];

function ageKey(days: number | null): AgeKey | null {
  if (days == null) return null;
  if (days <= 3) return "new";
  if (days <= 7) return "recent";
  return "old";
}
/* --------------------------------- */

function daysAgoNumber(date?: Date | string | null): number | null {
  if (!date) return null;
  const created = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(created.getTime())) return null;
  const c = new Date(created);
  const n = new Date();
  c.setHours(0, 0, 0, 0);
  n.setHours(0, 0, 0, 0);
  return Math.floor((n.getTime() - c.getTime()) / (1000 * 60 * 60 * 24));
}

function daysAgoText(date?: Date | string | null): string {
  const d = daysAgoNumber(date);
  return d == null ? "—" : String(d);
}

const sanitizeDigits = (s: string) => s.replace(/\D/g, "");

function matchesDaysFilter(actual: number | null, expr: string): boolean {
  if (actual == null) return false;
  const t = expr.trim();

  // "a-b"
  const range = t.match(/^(\d+)\s*-\s*(\d+)$/);
  if (range) {
    const a = parseInt(range[1], 10);
    const b = parseInt(range[2], 10);
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    return actual >= lo && actual <= hi;
  }

  const op = t.match(/^(<=|>=|<|>|=)?\s*(\d+)$/);
  if (op) {
    const sign = op[1] ?? "=";
    const n = parseInt(op[2], 10);
    if (Number.isNaN(n)) return false;
    switch (sign) {
      case "<":
        return actual < n;
      case "<=":
        return actual <= n;
      case ">":
        return actual > n;
      case ">=":
        return actual >= n;
      case "=":
      default:
        return actual === n;
    }
  }

  const n = parseInt(t, 10);
  if (!Number.isNaN(n)) return actual === n;

  return false;
}

function parseTruthy(val: string): boolean | null {
  const v = val.toLowerCase();
  if (["1", "true", "yes", "on", "map"].includes(v)) return true;
  if (["0", "false", "no", "off"].includes(v)) return false;
  return null;
}

interface Props {
  orders: OrderLocation[];
  hiddenOrderIds: Set<string>;
  onToggle: (orderId: string, show: boolean) => void;
  onShowAll: () => void;
  onRestore?: (hiddenOrderIds: Set<string>) => void;
  onFocusOrder: (orderId: string) => void;
}

type ColKey = "customer" | "daysAgo" | "phone" | "requestedDate" | "requestedBy" | "attempts" | "flagReason"
  | "assignedTo" | "assignedDate" | "assessmentDate";

const COLUMN_OPTIONS: { label: string; value: ColKey }[] = [
  { label: "Customer", value: "customer" },
  { label: "Days Ago", value: "daysAgo" },
  { label: "Phone Number", value: "phone" },
  { label: "Requested Delivery", value: "requestedDate"},
  { label: "Req. Updated By", value: "requestedBy"},
  { label: "No. Of Attempts", value: "attempts"},
  { label: "Reason For Flag", value: "flagReason"},
  { label: "Assigned to", value: "assignedTo" },
  { label: "Assigned date", value: "assignedDate" },
  { label: "Planned delivery date", value: "assessmentDate" },
];

// Base minimum widths per column (tweak to taste)
const COL_MIN_WIDTH: Record<ColKey | "onMap" | "orderId", number> = {
  onMap: 120,
  orderId: 120,
  customer: 180,
  daysAgo: 110,
  phone: 160,
  requestedDate: 180,
  requestedBy: 180,
  attempts: 120,
  flagReason: 220,
  assignedTo: 180,
  assignedDate: 170,
  assessmentDate: 180,
};

export default function OrderListPanel({
  orders,
  hiddenOrderIds,
  onToggle,
  onShowAll,
  onRestore,
  onFocusOrder,
}: Props) {
  const [visibleCols, setVisibleCols] = useState<ColKey[]>([]);
  const [search, setSearch] = useState<string>("");
  const processedAutoHideIds = useRef<Set<string>>(new Set());
  const [backupHidden, setBackupHidden] = useState<Set<string> | null>(null);
  const [flagReasons, setFlagReasons] = useState<Map<number, string>>(new Map());
  const fetchedFlagIds = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!visibleCols.includes("flagReason")) return;
    const ids = Array.from(new Set(
      orders.map(o => o.customerId).filter((id): id is number => id != null && !fetchedFlagIds.current.has(id))
    ));
    if (ids.length === 0) return;
    ids.forEach(id => fetchedFlagIds.current.add(id));
    getFlagReasonsByCustomer(ids)
      .then(res => setFlagReasons(prev => new Map([...Array.from(prev), ...Array.from(res)])))
      .catch(() => ids.forEach(id => fetchedFlagIds.current.delete(id)));
  }, [visibleCols, orders]);

  const handleColsChange = (e: MultiSelectChangeEvent) => {
    setVisibleCols(Array.isArray(e.value) ? (e.value as ColKey[]) : []);
  };

  // ======= Dynamic width calculation =======
  const [vw, setVw] = useState<number>(typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    const onR = () => setVw(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const PANEL_MIN = 320;
  const PANEL_MAX_VW = 0.40;
  const EXTRA = 80;
  const base = COL_MIN_WIDTH.onMap + COL_MIN_WIDTH.orderId;
  const dynamic = visibleCols.reduce((sum, k) => sum + COL_MIN_WIDTH[k], 0);
  const wantedPx = base + dynamic + EXTRA;
  const maxPx = Math.floor(vw * PANEL_MAX_VW);
  const panelWidthPx = Math.min(Math.max(wantedPx, PANEL_MIN), maxPx);
  const panelWidth = `${panelWidthPx}px`;
  const SEARCH_BASIS_PX = 360; // keep search width constant (tweak if you want 320/380/etc.)

  // ======= Filtering & rows =======
  const { frozenRows, tableRows, pinnedId, hasAnyRows } = useMemo(() => {
    const q = search.trim();
    const lower = q.toLowerCase();

    const matchesRow = (row: OrderLocation): boolean => {
      if (!lower) return true;

      const idStr = String(row.orderId ?? "");
      const name = (row.customerName ?? "").toString().toLowerCase();
      const phoneDigits = sanitizeDigits(String(row.primaryContactNbr ?? ""));
      const phonePretty = (row.primaryContactNbr ?? "").toString().toLowerCase();
      const days = daysAgoNumber(row.createdAt);
      const onMap = !hiddenOrderIds.has(idStr);

      const parts = lower.split(/\s+/).filter(Boolean);

      return parts.every((part) => {
        if (part.startsWith("id:")) {
          const v = part.slice(3).trim();
          return idStr.toLowerCase().includes(v);
        }
        if (part.startsWith("name:")) {
          const v = part.slice(5).trim();
          return name.includes(v);
        }
        if (part.startsWith("phone:") || part.startsWith("ph:")) {
          const v = sanitizeDigits(part.replace(/^phone:|^ph:/, "").trim());
          return v ? phoneDigits.includes(v) : false;
        }
        if (part.startsWith("days:")) {
          const v = part.slice(5).trim();
          return matchesDaysFilter(days, v);
        }
        if (part.startsWith("on:") || part.startsWith("map:") || part.startsWith("onmap:")) {
          const raw = part.replace(/^on:|^map:|^onmap:/, "").trim();
          const want = parseTruthy(raw);
          if (want == null) return true;
          return want ? onMap : !onMap;
        }

        if (/^\d+$/.test(part)) {
          return idStr.includes(part) || phoneDigits.includes(part) || matchesDaysFilter(days, part);
        }

        return name.includes(part) || phonePretty.includes(part) || idStr.includes(part);
      });
    };

    const filtered = lower ? orders.filter(matchesRow) : orders;

    let pinned: OrderLocation | null = null;
    let pinnedKey: string | null = null;
    if (lower) {
      const exactId = lower.startsWith("id:") ? lower.slice(3).trim()
        : /^\d+$/.test(lower) ? lower
        : "";
      if (exactId) {
        const exact = filtered.find(o => String(o.orderId) === exactId)
          ?? orders.find(o => String(o.orderId) === exactId);
        if (exact) {
          pinned = exact;
          pinnedKey = String(exact.orderId);
        }
      }
    }

    const rest = pinned ? filtered.filter(o => String(o.orderId) !== pinnedKey) : filtered;
    const hasAnyRows = rest.length > 0 || !!pinned;

    return {
      frozenRows: pinned ? [pinned] : [],
      tableRows: rest,
      pinnedId: pinnedKey,
      hasAnyRows
    };
  }, [orders, search, hiddenOrderIds, visibleCols]);

  /* ---------- Totals per bucket (for currently visible rows) ---------- */
  const ageTotals = useMemo(() => {
    const rows = [...frozenRows, ...tableRows];
    let newer = 0, recent = 0, older = 0;
    for (const r of rows) {
      const d = daysAgoNumber(r.createdAt);
      const k = ageKey(d);
      if (k === "new") newer++;
      else if (k === "recent") recent++;
      else if (k === "old") older++;
    }
    return { new: newer, recent, old: older, total: rows.length };
  }, [frozenRows, tableRows]);

  const dynamicColumns = useMemo(() => {
    const out: React.ReactNode[] = [];

    for (const key of ["assignedTo", "assignedDate", "assessmentDate"] as const) {
      if (visibleCols.includes(key)) {
        out.push(<Column key={key} field={key} header={COLUMN_OPTIONS.find(c => c.value === key)!.label}
          sortable style={{ minWidth: COL_MIN_WIDTH[key] + "px" }}
          body={(row: OrderLocation) => !row.isAssigned ? "—" : key === "assignedTo"
            ? row.assignedTo || "—" : assignmentDateText(row[key])} />);
      }
    }

    if (visibleCols.includes("customer")) {
      out.push(
        <Column
          key="customer"
          field="customerName"
          header="Customer"
          sortable
          style={{ minWidth: COL_MIN_WIDTH.customer + "px" }}
        />
      );
    }

    if (visibleCols.includes("daysAgo")) {
      out.push(
        <Column
          key="daysAgo"
          header="Days Ago"
          sortable
          sortField="createdAt"
          dataType="date"
          body={(row: OrderLocation) => daysAgoText(row.createdAt)}
          style={{ minWidth: COL_MIN_WIDTH.daysAgo + "px" }}
        />
      );
    }

    if (visibleCols.includes("phone")) {
      out.push(
        <Column
          key="phone"
          field="primaryContactNbr"
          header="Phone Number"
          sortable
          body={(row: OrderLocation) => row.primaryContactNbr || "—"}
          style={{ minWidth: COL_MIN_WIDTH.phone + "px" }}
        />
      );
    }

    if (visibleCols.includes("requestedDate")) {
      out.push(
        <Column
          key="requestedDate"
          field="requestedDate"
          header="Requested Delivery"
          sortable
          body={(row: OrderLocation) => {
            const val = row.requestedDate;
            if (!val) return "—";
            const date = typeof val === "string" ? new Date(val) : val;
            if (isNaN(date.getTime())) return "—";

            return date.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });
          }}
          style={{ minWidth: COL_MIN_WIDTH.requestedDate + "px" }}
        />
      );
    }

    if (visibleCols.includes("requestedBy")) {
      out.push(
        <Column
          key="requestedBy"
          field="requestedBy"
          header="Req. Updated By"
          sortable
          style={{ minWidth: COL_MIN_WIDTH.requestedBy + "px" }}
        />
      );
    }

    if (visibleCols.includes("attempts")) {
      out.push(
        <Column
          key="attempts"
          field="attempts"
          header="No. Of Attempts"
          sortable
          style={{ minWidth: COL_MIN_WIDTH.attempts + "px" }}
        />
      );
    }

    if (visibleCols.includes("flagReason")) {
      out.push(
        <Column
          key="flagReason"
          header="Reason For Flag"
          body={(row: OrderLocation) =>
            (row.customerId != null && flagReasons.get(row.customerId)) || "—"
          }
          style={{ minWidth: COL_MIN_WIDTH.flagReason + "px" }}
        />
      );
    }

    return out;
  }, [visibleCols, flagReasons]);

  // --------- AUTO-HIDE EFFECT (run once per order) ----------
  // Change: only auto-hide FUTURE requested dates; past and empty remain visible.
  useEffect(() => {
    const processed = processedAutoHideIds.current;
    const allIds = new Set(orders.map(o => String(o.orderId)));

    for (const id of allIds) {
      if (processed.has(id)) continue;
      const row = orders.find(o => String(o.orderId) === id);
      if (!row) continue;
      if (row.isAssigned) { processed.add(id); continue; }

      const reqVal = row.requestedDate;
      // if no requestedDate -> treat as empty -> do not auto-hide (leave visible)
      if (!reqVal) {
        processed.add(id);
        continue;
      }

      const reqDate = typeof reqVal === "string" ? new Date(reqVal) : reqVal;
      if (!isNaN(reqDate.getTime())) {
        const now = new Date();
        reqDate.setHours(0,0,0,0);
        now.setHours(0,0,0,0);

        // NEW: only hide if requestedDate is in the FUTURE
        const isFuture = reqDate.getTime() > now.getTime();
        if (isFuture && !hiddenOrderIds.has(id)) {
          onToggle(id, false);
        }
      }

      processed.add(id);
    }
  }, [orders.length]); // run only when count changes

  return (
    <div
      className="order-panel"
      style={{
        flex: `0 0 ${panelWidth}`,   
        width: panelWidth,
        maxWidth: panelWidth,
        border: "1px solid #ddd",
        borderRadius: "6px",
        padding: "8px",
        overflow: "hidden",
        minWidth: 0,
        transition: "width .2s ease",
      }}
    >
      {/* Row-color CSS */}
      <style>{`
        .p-datatable tr.age-new    > td { background: ${AGE_BUCKETS[0].bg}; }
        .p-datatable tr.age-recent > td { background: ${AGE_BUCKETS[1].bg}; }
        .p-datatable tr.age-old    > td { background: ${AGE_BUCKETS[2].bg}; }
      `}</style>

      <div className="smart-map-orders-header">
        <div className="smart-map-orders-heading">
          <div className="flex align-items-center gap-2">
            <h3 className="m-0">Orders <span className="smart-map-order-count">{frozenRows.length + tableRows.length}</span></h3>
            <ExportOrdersExcel
              orders={[...frozenRows, ...tableRows]}
              fileName={`Orders_${new Date().toISOString().slice(0, 10)}.xlsx`}
              variant="icon"
              style={{ width: 28, height: 28, marginLeft: 6 }}
              title="Export: Order ID, Customer, Days Ago, Phone Number, Requested Delivery, Requested By"
            />
          </div>

          <div className="smart-map-orders-visibility">
            <Button
              label={backupHidden === null ? "Show All on Map" : "Restore"}
              icon="pi pi-eye"            
              onClick={() => {
                if (backupHidden === null) {
                  // backup currently hidden orders
                  setBackupHidden(new Set(hiddenOrderIds));
                  // Use parent's function to SHOW ALL on map
                  onShowAll();  
                } else {
                  // SECOND CLICK → restore original hidden orders
                  if (onRestore) onRestore(backupHidden);
                  else orders.forEach(row => onToggle(String(row.orderId), !backupHidden.has(String(row.orderId))));
                  setBackupHidden(null);
                }
              }}
              className="p-button-text"
            />
          </div>
        </div>

        <div className="smart-map-orders-filters">
          <span
            className="p-input-icon-left p-input-icon-right flex-1"
            style={{ maxWidth: "320px", flexShrink: 1, flexBasis: SEARCH_BASIS_PX }}
            onClick={() => document.getElementById("keywordSearchInput")?.focus()}
          >
            <i className="pi pi-search" />

            <InputText
              id="keywordSearchInput"
              value={search}
              type="text"
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Keyword Search"
              className="w-full"
            />

            {search && (
              <i
                className="pi pi-times"
                onClick={(e) => {
                  e.stopPropagation(); // prevent parent click
                  setSearch("");
                }}
                style={{ cursor: "pointer" }}
                title="Clear"
              />
            )}
          </span>

          <MultiSelect
            aria-label="Choose order columns"
            value={visibleCols}
            options={COLUMN_OPTIONS}
            onChange={handleColsChange}
            optionLabel="label"
            optionValue="value"
            placeholder="Choose columns"
            display="chip"
            showClear
            maxSelectedLabels={2}
            className="flex-1 min-w-0"
            style={{ maxWidth: "600px" }}
            panelStyle={{ maxWidth: "min(90vw, 640px)" }}
          />
        </div>
      </div>

      <DataTable
        value={tableRows}
        frozenValue={frozenRows}
        emptyMessage={hasAnyRows ? <span /> : "No results found"}
        dataKey="orderId"
        scrollable
        scrollHeight="flex"
        size="small"
        responsiveLayout="scroll"
        stripedRows
        sortMode="multiple"
        removableSort
        sortField="orderId"
        sortOrder={1}
        rowClassName={(row: OrderLocation) => {
          const id = String(row.orderId);
          const d = daysAgoNumber(row.createdAt);
          const k = ageKey(d);
          return {
            "opacity-60": hiddenOrderIds.has(id),
            "p-highlight": !!(pinnedId && String(row.orderId) === pinnedId),
            "age-new": k === "new",
            "age-recent": k === "recent",
            "age-old": k === "old",
          } as any;
        }}
      >
        <Column
          header="On Map"
          body={(row: OrderLocation) => {
            const id = String(row.orderId);
            const reqVal = row.requestedDate;

            // If requestedDate is empty => considered past/visible by default.
            // If requestedDate is today or in the past => visible by default.
            // If requestedDate is in the future => auto-hidden by effect, but user can toggle to show.
            let isFuture = false;
            if (reqVal) {
              const reqDate = typeof reqVal === "string" ? new Date(reqVal) : reqVal;
              if (!isNaN(reqDate.getTime())) {
                const now = new Date();
                reqDate.setHours(0, 0, 0, 0);
                now.setHours(0, 0, 0, 0);
                isFuture = reqDate.getTime() > now.getTime();
              }
            }

            // Checkbox state is driven solely by whether the order id is hidden.
            // If it's in hiddenOrderIds => shown = false (unchecked). Otherwise shown = true (checked).
            const checked = !hiddenOrderIds.has(id);
            
            const tooltipText = isFuture
              ? (checked ? "Future requested delivery — currently shown (click to hide)" : "Future requested delivery — hidden by default (click to show)")
              : (checked ? "Visible on map (click to hide)" : "Hidden on map (click to show)");

            return (
              <div className="flex align-items-center gap-2">
                <Checkbox
                  inputId={`show_${id}`}
                  aria-label={`Show order ${id} on map`}
                  checked={checked}
                  onChange={(e) => onToggle(id, Boolean(e.checked))}
                  tooltip={tooltipText}
                  tooltipOptions={{ position: "top" }}
                />
              </div>
            );
          }}
          style={{ width: COL_MIN_WIDTH.onMap + "px" }}
        />
        <Column
          field="orderId"
          header="Order ID"
          sortable
          style={{ minWidth: COL_MIN_WIDTH.orderId + "px" }}
          body={(row: OrderLocation) => {
            const id = String(row.orderId ?? "");
            return id ? (
              <button
                type="button"
                className="p-button p-button-link p-0"
                onClick={() => onFocusOrder(id)}
                title="Focus this order on the map"
                style={{ cursor: "pointer" }}
              >
                {id}
              </button>
            ) : "—";
          }}
        />
        {dynamicColumns}
      </DataTable>
    </div>
  );
}
